// FieldColumnsHelpers.js

import { DataStore } from "@aws-amplify/datastore";
import { Forms } from "../models";
import { canWriteView } from "../permissions/Permissions";

// Local util to normalize indices (1..N)
const normalizeFieldsIndex = (fields) =>
  (fields || []).map((f, i) => ({ ...f, index: i + 1 }));

const clone = (x) => JSON.parse(JSON.stringify(x));

/**
 * Overwrite the fields for a view in `form.meta.views`.
 * Uses saveFormPartial so all writes go through one persistence path.
 */
export const handleViewFieldsChangeHelper = async (form, viewLabel, nextFields) => {
  if (!form) return { error: "Form not found." };
  if (!form.id) return { error: "Form id missing." };

  const views = form.meta?.views;
  if (!Array.isArray(views)) return { error: "Views missing or invalid on form." };

  const hasView = views.some((v) => v?.viewLabel === viewLabel);
  if (!hasView) return { error: `View "${viewLabel}" not found.` };

  const normalized = normalizeFieldsIndex(nextFields);

  try {
    const newViews = views.map((v) =>
      v?.viewLabel === viewLabel ? { ...v, fields: normalized } : v
    );

    // Persist only meta.views via your existing partial saver
    await saveFormPartial(form.id, { meta: { views: newViews } });

    return { ok: true, fields: normalized };
  } catch (e) {
    return { error: `handleViewFieldsChangeHelper: ${e?.message || e}` };
  }
};

/**
 * Update one field's width inside view.fields then persist via handleViewFieldsChangeHelper.
 */
export const handleFieldWidthChangeHelper = async (form, viewLabel, field, newWidth) => {
  if (!form) return { error: "Form not found." };

  const v = form.meta?.views?.find((x) => x?.viewLabel === viewLabel);
  if (!v) return { error: "Selected view not found." };

  const next = (v.fields || []).map((f) =>
    f.field === field ? { ...f, width: newWidth } : f
  );

  return handleViewFieldsChangeHelper(form, viewLabel, next);
};

/**
 * Persist a new ordered fields array.
 * Expecting `newFields` already in the desired order.
 */
export const handleFieldOrderChangeHelper = async (form, viewLabel, newFields) => {
  return handleViewFieldsChangeHelper(form, viewLabel, newFields);
};

/**
 * saveFormPartial(formId, partial)
 *
 * What this function does:
 * - Reads the current Forms record from DataStore by id.
 * - Applies a partial update to the record using Forms.copyOf + DataStore.save.
 * - Supports updating:
 *    - pages: serialized via serializePagesForPersist(...)
 *    - dictionaries: sanitized via sanitizeDictionariesForPersist(...)
 *    - meta: shallow-merged into existing draft.meta (clone(partial.meta))
 * - Does NOT attempt to validate business rules for meta sub-structures (e.g. meta.views).
 * - Throws on invalid inputs, query failures, missing form, or save failures.
 *
 * Notes:
 * - This is a generic persistence helper; callers are responsible for supplying a correct partial.
 * - Meta merge is shallow at the meta object level; nested objects are not deep-merged.
 */
export const saveFormPartial = async (formId, partial) => {
  if (!formId) throw new Error("saveFormPartial: formId is required");
  if (!partial || typeof partial !== "object") {
    throw new Error("saveFormPartial: partial update object is required");
  }

  let current;
  try {
    current = await DataStore.query(Forms, formId);
  } catch (err) {
    console.error("DataStore.query failed:", err);
    throw err;
  }
  if (!current) throw new Error(`saveFormPartial: form not found for id ${formId}`);

  try {
    return await DataStore.save(
      Forms.copyOf(current, (draft) => {
        if (Object.prototype.hasOwnProperty.call(partial, "pages")) {
          try {
            draft.pages = serializePagesForPersist(partial.pages);
          } catch (err) {
            console.error("serializePagesForPersist failed:", err);
            throw err;
          }
        }

        if (Object.prototype.hasOwnProperty.call(partial, "dictionaries")) {
          try {
            draft.dictionaries = sanitizeDictionariesForPersist(partial.dictionaries);
          } catch (err) {
            console.error("sanitizeDictionariesForPersist failed:", err);
            throw err;
          }
        }

        if (partial.meta && typeof partial.meta === "object") {
          try {
            // Clone from current (canonical, pre-draft) to avoid reading
            // from the Amplify proxy which may reflect optimistic state.
            const existingMeta = clone(current.meta || {});
            const incomingMeta = clone(partial.meta);

            console.log("[saveFormPartial] meta.formLanguages BEFORE merge:", JSON.stringify(existingMeta.formLanguages));

            draft.meta = { ...existingMeta, ...incomingMeta };

            console.log("[saveFormPartial] meta.formLanguages AFTER merge:", JSON.stringify(draft.meta.formLanguages));
          } catch (err) {
            console.error("meta merge failed:", err);
            throw err;
          }
        }
      })
    );
  } catch (err) {
    console.error("DataStore.save failed:", err);
    throw err;
  }
};


/**
 * applyFieldsChange(formOrId, viewLabel, change, opts?)
 *
 * What this function does:
 * - Reads the canonical form from DataStore and targets ONLY meta.views[idx].fields.
 * - Locates the view by exact viewLabel match in meta.views.
 * - Enforces write permissions for that view using canWriteView({ write: view.write, userId, isSuperAdmin })
 *   when opts.auth is provided.
 * - Applies a supported operation to fields, normalizes index 1..N, persists via saveFormPartial.
 * - Returns { ok: true, fields } on success.
 * - Returns { ok: false, code, error } on any failure path (including permissions).
 */
export const applyFieldsChange = async (formOrId, viewLabel, change, opts = {}) => {
  try {
    const formId = typeof formOrId === "string" ? formOrId : formOrId?.id;
    if (!formId) return { ok: false, code: "BAD_INPUT", error: "applyFieldsChange: formId is required" };
    if (!viewLabel) return { ok: false, code: "BAD_INPUT", error: "applyFieldsChange: viewLabel is required" };

    // Allow callers to send either a change object or a raw array => replace-all
    let normalizedChange = change;
    if (Array.isArray(change)) {
      normalizedChange = { op: "replace-all", fields: change };
    }

    // Load current form (canonical)
    const current = await DataStore.query(Forms, formId);
    if (!current) {
      return { ok: false, code: "NOT_FOUND", error: `applyFieldsChange: form not found for id=${formId}` };
    }

    const metaIn = current.meta || {};
    const viewsIn = Array.isArray(metaIn.views) ? metaIn.views : [];
    const idx = viewsIn.findIndex((v) => v?.viewLabel === viewLabel);

    if (idx < 0) {
      console.warn(
        "[applyFieldsChange] view not found; available labels:",
        viewsIn.map((v) => v?.viewLabel)
      );
      return { ok: false, code: "VIEW_NOT_FOUND", error: `view "${viewLabel}" not found in meta.views` };
    }

    const view = viewsIn[idx] || {};

    // ---- Permission enforcement (view-level) ----
    // opts.auth must be provided by the caller (e.g. App.js handlers / ViewManager)
    const auth = opts?.auth;
    if (!auth || typeof auth !== "object") {
      return {
        ok: false,
        code: "AUTH_CONTEXT_MISSING",
        error: "applyFieldsChange: opts.auth { userId, isSuperAdmin } is required for permission enforcement",
      };
    }

    const userId = auth?.userId;
    const isSuperAdmin = !!auth?.isSuperAdmin;

    const allowed = canWriteView({ write: view?.write, userId, isSuperAdmin });
    if (!allowed) {
      return {
        ok: false,
        code: "PERMISSION_DENIED",
        error: `Permission denied: user cannot modify fields for view "${viewLabel}"`,
      };
    }

    // ---- Apply operation ----
    let next = Array.isArray(view.fields) ? clone(view.fields) : [];
    const normalizeIndex = (flds) => (flds || []).map((f, i) => ({ ...f, index: i + 1 }));

    switch (normalizedChange?.op) {
      case "replace-all": {
        const flds = Array.isArray(normalizedChange.fields) ? normalizedChange.fields : [];
        next = normalizeIndex(flds);
        break;
      }

      case "toggle-visible": {
        const fieldKey = normalizedChange?.field;
        next = normalizeIndex(
          next.map((f) => (f.field === fieldKey ? { ...f, visible: !f.visible } : f))
        );
        break;
      }

      case "reorder": {
        // In your system, fields are colDefs, so reorder expects the full ordered colDef array.
        const flds = Array.isArray(normalizedChange?.fields) ? normalizedChange.fields : [];
        next = normalizeIndex(flds);
        break;
      }

      case "rename": {
        const { field: fieldKey, headerName } = normalizedChange || {};
        next = normalizeIndex(
          next.map((f) => (f.field === fieldKey ? { ...f, headerName } : f))
        );
        break;
      }

      case "remove": {
        const fieldKey = normalizedChange?.field;
        next = normalizeIndex(next.filter((f) => f.field !== fieldKey));
        break;
      }

      case "add": {
        const newField = normalizedChange?.newField;
        if (newField && !next.some((f) => f.field === newField.field)) {
          next = normalizeIndex([...next, { ...newField, visible: newField.visible ?? true }]);
        }
        break;
      }

      case "set-prop": {
        const fieldKey = normalizedChange?.field;
        const patch = normalizedChange?.patch;

        if (!fieldKey || !patch || typeof patch !== "object") {
          return { ok: false, code: "BAD_INPUT", error: 'set-prop requires { field: string, patch: object }' };
        }

        next = normalizeIndex(
          next.map((f) => (f.field === fieldKey ? { ...f, ...patch } : f))
        );
        break;
      }

      default: {
        if (normalizedChange && typeof normalizedChange === "object") {
          console.warn("[applyFieldsChange] unknown op, skipping", normalizedChange?.op);
        }
        // No-op: still persists current fields unless you want to block unknown ops.
        break;
      }
    }

    const newViews = viewsIn.slice();
    newViews[idx] = { ...view, fields: next };

    await saveFormPartial(formId, { meta: { views: newViews } });

    if (opts.verify) {
      const verify = await DataStore.query(Forms, formId);
      const vView = verify?.meta?.views?.find((v) => v?.viewLabel === viewLabel);
      return { ok: true, fields: next, verified: !!vView };
    }

    return { ok: true, fields: next };
  } catch (e) {
    console.error("[applyFieldsChange] failed", e);
    return { ok: false, code: "EXCEPTION", error: `applyFieldsChange failed: ${e?.message || e}` };
  }
};


/**
 * applyViewsChange(formOrId, change)
 *
 * What this function does:
 * - Persists changes ONLY to: form.meta.views (entire views array) for the given form.
 * - Always reads the canonical form from DataStore first (ignores passed-in form contents other than id).
 * - Sources views strictly from meta.views (no fallback paths).
 * - Applies one of the supported view-level operations to produce nextViews.
 * - Ensures a single default view when:
 *    - replacing all views
 *    - adding a view that is default
 *    - patching a view with defaultView=true
 * - Persists via saveFormPartial(formId, { meta: nextMeta }).
 * - Returns { ok: true, views: nextViews } on success; { error } on failure.
 *
 * Supported operations:
 * - replace-all: { op, views: View[] }  (ensures single default)
 * - add: { op, view: View }            (stamps updatedAt; ensures single default if defaultView=true)
 * - delete: { op, viewLabel: string }
 * - set-default: { op, viewLabel: string } (sets that view defaultView=true and others false; bumps updatedAt only on the default target)
 * - patch: { op, viewLabel: string, patch: object } (bumps updatedAt and optionally version; ensures single default if defaultView=true)
 *
 * Notes:
 * - This function currently does not enforce permissions by itself.
 * - It warns (console.warn) if duplicate viewLabel values exist after the operation.
 */
export const applyViewsChange = async (formOrId, change) => {
  try {
    const formId = typeof formOrId === "string" ? formOrId : formOrId?.id;
    if (!formId) return { error: "applyViewsChange: formId is required" };

    // 1) Fetch canonical record
    const current = await DataStore.query(Forms, formId);
    if (!current) return { error: `applyViewsChange: form not found for id=${formId}` };

    // 2) Source strictly from meta.views (no fallback anymore)
    const meta = clone(current.meta || {});
    let views = Array.isArray(meta.views) ? clone(meta.views) : [];

    const ensureSingleDefault = (arr) => {
      const firstIdx = arr.findIndex((v) => !!v?.defaultView);
      if (firstIdx === -1) return arr;
      let seen = false;
      return arr.map((v) => {
        if (v?.defaultView) {
          if (!seen) {
            seen = true;
            return v;
          }
          return { ...v, defaultView: false };
        }
        return v;
      });
    };

    const bump = (v, { bumpVersion = true } = {}) => ({
      ...v,
      updatedAt: new Date().toISOString(),
      ...(bumpVersion ? { version: (Number(v?.version) || 1) + 1 } : {}),
    });

    let nextViews = views;

    switch (change?.op) {
      case "replace-all": {
        const incoming = change?.views;
        if (!Array.isArray(incoming)) return { error: "replace-all requires { views: View[] }" };
        nextViews = ensureSingleDefault(clone(incoming));
        break;
      }

      case "add": {
        const { view } = change || {};
        if (!view || typeof view !== "object") return { error: "add requires { view: View }" };
        const stamped = {
          ...view,
          updatedAt: new Date().toISOString(),
          version: Number(view?.version) || 1,
        };
        nextViews = [...views, stamped];
        if (stamped.defaultView) {
          nextViews = nextViews.map((v) => ({
            ...v,
            defaultView: v.viewLabel === stamped.viewLabel,
          }));
        }
        break;
      }

      case "delete": {
        const { viewLabel: delLabel } = change || {};
        if (!delLabel) return { error: "delete requires { viewLabel }" };
        nextViews = views.filter((v) => v?.viewLabel !== delLabel);
        break;
      }

      case "set-default": {
        const { viewLabel: defLabel } = change || {};
        if (!defLabel) return { error: "set-default requires { viewLabel }" };
        nextViews = views.map((v) =>
          v.viewLabel === defLabel
            ? bump({ ...v, defaultView: true }, { bumpVersion: false })
            : { ...v, defaultView: false }
        );
        break;
      }

      case "patch": {
        const { viewLabel: patchLabel, patch } = change || {};
        if (!patchLabel || !patch || typeof patch !== "object") {
          return { error: "patch requires { viewLabel, patch: object }" };
        }
        nextViews = views.map((v) => (v.viewLabel === patchLabel ? bump({ ...v, ...patch }) : v));
        if (patch.defaultView === true) {
          nextViews = nextViews.map((v) => ({ ...v, defaultView: v.viewLabel === patchLabel }));
        }
        break;
      }

      default:
        return { error: `unsupported op "${change?.op}"` };
    }

    // warn on duplicates
    {
      const labels = new Set();
      const dups = [];
      for (const v of nextViews) {
        const lbl = String(v?.viewLabel || "");
        if (labels.has(lbl)) dups.push(lbl);
        else labels.add(lbl);
      }
      if (dups.length) console.warn("[applyViewsChange] duplicate viewLabel(s):", dups);
    }

    const nextMeta = { ...meta, views: nextViews };

    console.log("[applyViewsChange] persist", {
      formId,
      op: change?.op,
      persistPath: "meta.views",
      nextCount: nextViews.length,
    });

    await saveFormPartial(formId, { meta: nextMeta });

    return { ok: true, views: nextViews };
  } catch (e) {
    return { error: `applyViewsChange failed: ${e?.message || e}` };
  }
};

/**
 * serializeControlForPersist(ctrl, pageIdx, ctrlIdx)
 * - ctrl: control object to serialize
 * - pageIdx: page index (for error logging)
 * - ctrlIdx: control index (for error logging)
 *
 * Prepares a control for DataStore persistence:
 * - Stringifies 'attributes' (objects → JSON string for AWSJSON schema)
 * - Stringifies 'options' array items (objects → JSON strings)
 * - Removes all null/undefined fields to prevent schema pollution
 */
function serializeControlForPersist(ctrl, pageIdx, ctrlIdx) {
  const out = { ...ctrl };
  if (Object.prototype.hasOwnProperty.call(out, "attributes")) {
    const a = out.attributes;
    if (a === null || a === undefined) {
      delete out.attributes;
    } else if (typeof a === "object") {
      try {
        out.attributes = JSON.stringify(a);
      } catch (err) {
        console.error(`Failed to stringify attributes at page ${pageIdx}, control ${ctrlIdx}:`, err, a);
        out.attributes = "{}";
      }
    } else {
      out.attributes = String(a);
    }
  }
  if (Object.prototype.hasOwnProperty.call(out, "options")) {
    const opt = out.options;
    if (opt === null || opt === undefined) {
      delete out.options;
    } else if (Array.isArray(opt)) {
      out.options = opt.map((item, i) => {
        if (item == null) return null;
        if (typeof item === "object") {
          try {
            return JSON.stringify(item);
          } catch (err) {
            console.error(
              `Failed to stringify option at page ${pageIdx}, control ${ctrlIdx}, option ${i}:`,
              err,
              item
            );
            return "{}";
          }
        }
        return String(item);
      });
    } else if (typeof opt === "string") {
      out.options = opt;
    } else {
      out.options = String(opt);
    }
  }
  Object.keys(out).forEach(key => {
    if (out[key] === null || out[key] === undefined) {
      delete out[key];
    }
  });
  return out;
}

/**
 * serializePagesForPersist(pages)
 * - pages: array of page objects (or non-array, normalized to [])
 *
 * Prepares pages array for DataStore persistence:
 * - Iterates all pages and their controls
 * - Calls serializeControlForPersist for each control
 * - Provides error recovery (bad controls → minimal valid fallback)
 */
function serializePagesForPersist(pages) {
  const src = Array.isArray(pages) ? pages : [];
  return src.map((p, pi) => ({
    ...p,
    controls: Array.isArray(p.controls)
      ? p.controls.map((c, ci) => {
          try {
            return serializeControlForPersist(c, pi, ci);
          } catch (err) {
            console.error(`serializeControlForPersist failed at page ${pi}, control ${ci}:`, err, c);
            return {
              type: String(c?.type ?? "").toUpperCase() || "TEXT",
              key: c?.key ?? `ctrl_${pi}_${ci}`,
              required: !!c?.required,
              attributes: "{}",
            };
          }
        })
      : [],
  }));
}

function sanitizeDictionariesForPersist(dicts) {
  if (!Array.isArray(dicts)) return dicts;
  return dicts.map((d) => {
    if (!d || typeof d !== "object") return d;
    const out = { ...d };
    if ("phrases" in out) {
      out.phrases = toAwsJsonString(out.phrases);
    }
    return out;
  });
}

function toAwsJsonString(obj) {
  if (obj === null || obj === undefined) return null;
  if (typeof obj === "string") return obj;
  try {
    return JSON.stringify(obj);
  } catch (err) {
    console.error("Failed to stringify AWSJSON field:", err, obj);
    return "{}";
  }
}
